import {
  MAX_CHARACTER_EMAIL_VALIDATION,
  MAX_CHARACTER_FULLNAME_VALIDATION,
  MAX_CHARACTER_INPUT_VALIDATION,
  MAX_CHARACTER_PHONE_VALIDATION,
  MAX_CHARACTER_ZIP_CODE,
  MAX_COMPANY_ADDRESS_INPUT_VALIDATION,
  MAX_TEXT_INPUT_VALIDATION,
} from "@/constants/common";
import { INVALID_NUMBER_MAX, INVALID_NUMBER_MIN } from "@/constants/invalids";
import {
  MAX_SPECIFIC_SKILL_TRANSITION_MONTH_PERIOD,
  MAX_SPECIFIC_SKILL_TRANSITION_YEAR_PERIOD,
  SPECIFIC_SKILL_TRANSITION_PERIOD_ERROR_MESSAGE,
} from "@/features/talent/constants/form";
import { calculateSpecificSkillTransitionPeriod } from "@/features/talent/utils/calculateSpecificSkillTransitionPeriod";
import { ERecruitmentStatus, TALENT_LOCATION } from "@/types/enum";
import { replaceStr } from "@/utils/string";
import { validator } from "@/utils/validator";
import dayjs from "dayjs";
import { useMemo } from "react";

export const useGetTalentSchema = () => {
  const talentSchema = useMemo(() => {
    return validator.object().shape({
      recruitmentStatus: validator.number().isSelection().emptyToNull().label("求職活動ステータス").required(),
      fullName: validator
        .string()
        .trim()
        .emptyToNull()
        .label("氏名")
        .nameOnly()
        .latinsOnly()
        .autoTransformToUpperCase()
        .required()
        .max(MAX_CHARACTER_FULLNAME_VALIDATION),
      isRecommendedTalent: validator.boolean().notRequired().optional().label(""),
      birthday: validator.mixed().isDayjs().isSelection().isBeforeAge(18).nullable().label("生年月日").notRequired(),
      nickName: validator.string().trim().emptyToNull().katakanaOnly().label("呼び名").notRequired().max(MAX_CHARACTER_FULLNAME_VALIDATION),
      talentNumber: validator
        .string()
        .trim()
        .emptyToNull()
        .notComma()
        .removeSpaces()
        .label("人材管理番号")
        .notRequired()
        .max(MAX_CHARACTER_INPUT_VALIDATION),
      nationalityId: validator
        .number()
        .isSelection()
        .emptyToNull()
        .label("国籍")
        .optional()
        .when("recruitmentStatus", {
          is: (val: ERecruitmentStatus) => val !== ERecruitmentStatus.DRAFT,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      gender: validator
        .number()
        .isSelection()
        .emptyToNull()
        .label("性別")
        .optional()
        .when("recruitmentStatus", {
          is: (val: ERecruitmentStatus) => val !== ERecruitmentStatus.DRAFT,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      phoneNumber: validator
        .string()
        .emptyToNull()
        .isTelNumber()
        .label("電話番号")
        .autoTransformToHalfWidth()
        .notRequired()
        .max(MAX_CHARACTER_PHONE_VALIDATION),
      email: validator.string().email().emptyToNull().label("メールアドレス").notRequired().max(MAX_CHARACTER_EMAIL_VALIDATION),
      religion: validator.string().trim().emptyToNull().label("宗教").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION),
      location: validator
        .number()
        .isSelection()
        .optional()
        .emptyToNull()
        .label("居住地")
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .when("recruitmentStatus", {
          is: (val: ERecruitmentStatus) => val !== ERecruitmentStatus.DRAFT,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      zipCode: validator
        .string()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .zipCode()
        .label("郵便番号")
        .optional()
        .max(MAX_CHARACTER_ZIP_CODE)
        .when(["recruitmentStatus", "location"], {
          is: (recruitmentStatus: ERecruitmentStatus, location: number) =>
            recruitmentStatus !== ERecruitmentStatus.DRAFT && location === TALENT_LOCATION.LOCAL,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      prefecture: validator.string().trim().emptyToNull().label("所在地（都道府県）").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION),
      city: validator.string().trim().emptyToNull().label("所在地（市区町村）").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION),
      address: validator
        .string()
        .trim()
        .emptyToNull()
        .nullable()
        .label("所在地（町名丁目番地号等）")
        .autoTransformToFullWidth()
        .notRequired()
        .max(MAX_COMPANY_ADDRESS_INPUT_VALIDATION)
        .when(["recruitmentStatus", "location"], {
          is: (recruitmentStatus: ERecruitmentStatus, location: number) =>
            recruitmentStatus !== ERecruitmentStatus.DRAFT && location === TALENT_LOCATION.LOCAL,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      country: validator
        .string()
        .trim()
        .emptyToNull()
        .nullable()
        .label("国・地域")
        .notRequired()
        .max(MAX_COMPANY_ADDRESS_INPUT_VALIDATION)
        .autoTransformToFullWidth(),
      periodOfStay: validator.mixed().isDayjs().isSelection().nullable().label("在留期限").notRequired(),
      specifiedSkillFirstStartDate: validator.mixed().isDayjs().isSelection().isPastOrToday().label("特定技能1号起算日").notRequired().nullable(),
      blankPeriodMonthly: validator
        .string()
        .trim()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .numbersOnly()
        .min(0, replaceStr(INVALID_NUMBER_MIN, { path: "ブランク期間（月単位）", min: 0 }))
        .maxValue(600, replaceStr(INVALID_NUMBER_MAX, { path: "ブランク期間（月単位）", max: 600 }))
        .label("ブランク期間（月単位）")
        .notRequired(),
      specifiedSkillFirstRemainingTerm: validator
        .string()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .label("特定技能1号残存期間")
        .nullable()
        .notRequired()
        .when(["specifiedSkillFirstStartDate", "blankPeriodMonthly"], ([specifiedSkillFirstStartDate, blankPeriodMonthly], schema) => {
          return schema.test("checkSpecifiedSkillFirstRemainingTerm", "", function () {
            const { path, createError } = this;

            const { monthPeriod, yearPeriod } = calculateSpecificSkillTransitionPeriod({
              specifiedSkillFirstStartDate: specifiedSkillFirstStartDate,
              blankPeriodMonthly: blankPeriodMonthly?.toString() || "0",
            });

            if (
              yearPeriod > MAX_SPECIFIC_SKILL_TRANSITION_YEAR_PERIOD ||
              (yearPeriod === MAX_SPECIFIC_SKILL_TRANSITION_YEAR_PERIOD && monthPeriod > MAX_SPECIFIC_SKILL_TRANSITION_MONTH_PERIOD)
            ) {
              return createError({ path, message: () => SPECIFIC_SKILL_TRANSITION_PERIOD_ERROR_MESSAGE });
            } else {
              return true;
            }
          });
        }),
      passportNumber: validator
        .string()
        .trim()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .autoTransformToUpperCase()
        .removeSpaces()
        .uppercaseAlphanumericCharactersOnly()
        .max(9)
        .label("パスポート番号")
        .notRequired()
        .test((value, ctx) => {
          if (ctx.parent.passportExpired && !ctx.parent.passportNumber) return ctx.createError({ message: "「パスポート番号」を入力してください。" });
          return true;
        }),
      passportExpiryDate: validator
        .mixed()
        .isSelection()
        .isDayjs()
        .label("パスポート期限")
        .notRequired()
        .test((value, ctx) => {
          if (!ctx.parent.passportExpiryDate && ctx.parent.passportCode) return ctx.createError({ message: "「旅券有効期限」を選択してください。" });
          return true;
        }),
      residenceStatusId: validator.number().isSelection().label("在留資格").notRequired().nullable(),
      educationBackground: validator.array().of(validator.number().isSelection().label("学歴").emptyToNull().nullable()).notRequired(),
      heldQualifications: validator
        .array()
        .of(validator.string().label("資格").nullable().emptyToNull().max(MAX_CHARACTER_INPUT_VALIDATION))
        .notRequired(),
      languageTestLevelIds: validator
        .array()
        .label("日本語能力証明書")
        .of(validator.number().isSelection().emptyToNull().nullable())
        .notRequired()
        .nullable(),
      languageTestOther: validator
        .string()
        .trim()
        .emptyToNull()
        .nullable()
        .label("日本語能力（その他）")
        .notRequired()
        .max(MAX_CHARACTER_INPUT_VALIDATION),
      specificSkillCertificates: validator.array().of(validator.number().emptyToNull().nullable()).label("特定技能合格証等").notRequired().nullable(),
      companySpecificIndustryId: validator
        .number()
        .isSelection()
        .label("分野")
        .emptyToNull()
        .nullable()
        .notRequired()
        .when("recruitmentStatus", {
          is: (val: ERecruitmentStatus) => val !== ERecruitmentStatus.DRAFT,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.notRequired(),
        }),
      specificBusinessClassificationIds: validator
        .array()
        .of(validator.number().isSelection().emptyToNull().nullable())
        .label("区分")
        .nullable()
        .notRequired(),
      majorId: validator.number().label("職種").notRequired().nullable(),
      jobTypeId: validator.number().label("作業").notRequired().nullable(),
      languageSkills: validator
        .array()
        .of(
          validator.object().shape({
            language: validator.number().isSelection().label("語学（日本語以外の言語）").nullable().emptyToNull().notRequired(),
            content: validator
              .string()
              .trim()
              .emptyToNull()
              .label("語学（日本語以外の言語）の内容")
              .nullable()
              .max(MAX_CHARACTER_INPUT_VALIDATION)
              .notRequired(),
          }),
        )
        .notRequired()
        .nullable(),
      workExperiences: validator
        .array()
        .of(
          validator.object().shape({
            jobType: validator.string().nullable().label("職種").max(MAX_CHARACTER_INPUT_VALIDATION).notRequired(),
            companyName: validator.string().nullable().label("企業名").max(MAX_CHARACTER_INPUT_VALIDATION).notRequired(),
            startDate: validator
              .mixed()
              .isDayjs()
              .isSelection()
              .nullable()
              .notRequired()
              .test("is-before-end", "「入社年月」は「退職年月」より前にする必要があります", function (value) {
                const { endDate } = this.parent;
                if (!value || !endDate) return true;
                return dayjs(value).isBefore(endDate) || dayjs(value).isSame(endDate);
              }),
            endDate: validator
              .mixed()
              .isDayjs()
              .isSelection()
              .nullable()
              .notRequired()
              .test("is-after-start", "「退職年月」は「入社年月」より後にする必要があります。", function (value) {
                const { startDate } = this.parent;
                if (!value || !startDate) return true;
                return dayjs(value).isAfter(startDate) || dayjs(value).isSame(startDate);
              }),
          }),
        )
        .optional()
        .nullable(),
      memo: validator.string().emptyToNull().label("特性メモ").nullable().max(MAX_TEXT_INPUT_VALIDATION).notRequired(),
      interviewEvaluation: validator.string().label("面談評価").emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION).notRequired(),
      familyInformation: validator.string().label("家族構成").emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION).notRequired(),
      desiredJobTypeIds: validator
        .array()
        .of(validator.number().isSelection().emptyToNull().nullable())
        .notRequired()
        .nullable()
        .label("希望職種（分野）"),
      desiredWorkLocation: validator.string().emptyToNull().nullable().notRequired().label("希望勤務地").max(MAX_CHARACTER_INPUT_VALIDATION),
      expectSalary: validator.string().emptyToNull().nullable().notRequired().max(MAX_CHARACTER_INPUT_VALIDATION).label("希望給与"),
      availableFrom: validator.string().emptyToNull().label("転職可能時期").notRequired().max(MAX_CHARACTER_INPUT_VALIDATION).nullable(),
      otherRequest: validator.string().trim().emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION).notRequired().label("その他希望"),
      internalMemo: validator.string().trim().emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION).notRequired().label("社内メモ"),
      interviewDate: validator.mixed().isSelection().isDayjs().label("面談日").notRequired().nullable(),
      interviewerId: validator.number().emptyToNull().nullable().notRequired().label("面談担当者"),
      partnerId: validator.number().label("パートナー名").notRequired().nullable(),
      personInChargeId: validator.number().isSelection().label("人材担当者").required(),
      relatedFileTypes: validator.array().of(validator.string().nullable().isSelection().required().label("人材添付書類の種類")).nullable(),
      partnerNationality: validator.number().isSelection().emptyToNull().nullable().notRequired().label("国籍"),
      partnerZipCode: validator.string().emptyToNull().nullable().notRequired().label("郵便番号"),
      partnerPrefecture: validator.string().emptyToNull().nullable().notRequired().label("都道府県"),
      partnerCity: validator.string().emptyToNull().nullable().notRequired().label("市区町村"),
      partnerAddress: validator.string().emptyToNull().nullable().notRequired().label("都道府県・市区町村以外の住所"),
      partnerLocation: validator.string().emptyToNull().nullable().notRequired().label("所在地"),
      partnerContactPerson: validator.string().emptyToNull().nullable().notRequired().label("担当者"),
      partnerContactMethod: validator.number().isSelection().emptyToNull().nullable().notRequired().label("連絡方法"),
      partnerPhoneNumber: validator.string().emptyToNull().nullable().notRequired().label("連絡先"),
      partnerEmail: validator.string().emptyToNull().nullable().notRequired().label("メールアドレス"),
      partnerOtherContacts: validator.string().emptyToNull().nullable().notRequired().label("その他の連絡先"),
      partnerLaborBureauStatus: validator.boolean().notRequired().label("労働局届出状況"),
      partnerContractInfo: validator.string().emptyToNull().nullable().notRequired().label("契約情報"),
      partnerBillingInfo: validator.string().emptyToNull().nullable().notRequired().label("請求情報"),
      partnerMemo: validator.string().emptyToNull().nullable().notRequired().label("メモ"),
    });
  }, []);

  return { talentSchema };
};
